import { theme } from '@/components';
import type { IndicatorsModel } from '@/features/project/projects/_models/activity';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type IndicatorSectionProps = {
  yAxisTitle: string;
  consecutiveIndicators: z.infer<typeof IndicatorsModel>;
};

export const IndicatorLineChart: FC<IndicatorSectionProps> = ({
  yAxisTitle,
  consecutiveIndicators,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const targetData = consecutiveIndicators.map((indicator) => [
    Date.UTC(indicator.fiscalYear, 0, 1),
    indicator.targetValue,
  ]);
  const actualData = consecutiveIndicators.map((indicator) => [
    Date.UTC(indicator.fiscalYear, 0, 1),
    indicator.actualValue,
  ]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      title: { text: '' },
      chart: {
        type: 'line',
        height: 'auto',
        backgroundColor: 'transparent',
      },
      xAxis: {
        type: 'datetime',
        startOnTick: true,
        endOnTick: true,
        tickInterval: 365 * 24 * 3600 * 1000,
        dateTimeLabelFormats: {
          year: '%Y',
          month: '%Y',
          day: '%Y',
        },
        title: {
          text: t('chart-year'),
        },
        minPadding: 0,
        maxPadding: 0,
        labels:
          targetData.length >= 10
            ? {
                rotation: -45,
                style: { fontSize: '11px' },
                overflow: 'justify',
              }
            : {},
      },
      yAxis: {
        title: {
          text: yAxisTitle,
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'left',
        verticalAlign: 'bottom',
        itemMarginTop: 16,
        itemStyle: { fontWeight: 'normal' },
      },
      series: [
        {
          type: 'line',
          name: t('indicator-target-value'),
          connectNulls: true,
          color: token.cyan,
          dashStyle: 'Dot',
          marker: {
            radius: 4,
            symbol: 'circle',
            states: {
              hover: {
                animation: {
                  duration: 400,
                },
              },
            },
          },
          data: targetData,
        },
        {
          type: 'line',
          name: t('indicator-actual-value'),
          connectNulls: true,
          color: '#006D75',
          marker: {
            radius: 4,
            symbol: 'circle',
            states: {
              hover: {
                animation: {
                  duration: 400,
                },
              },
            },
          },
          data: actualData,
        },
      ],
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
    }),
    [targetData, actualData, token.cyan, t, yAxisTitle],
  );

  return (
    (targetData.length >= 1 || actualData.length >= 1) && (
      <HighchartsReact highcharts={Highcharts} options={options} />
    )
  );
};
